import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { DialogService } from 'primeng/dynamicdialog';

import { DataService, DateService, HelperService, HTTPService } from 'app/services';
import { mileageUnits } from 'app/constants';

import { find, isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { AddTireStorageModal } from '../add-tire-storage/add-tire-storage.component';

@Component({
  selector: 'app-vehicle-side-list',
  templateUrl: './vehicle-side-list.component.html',
  styleUrls: ['./vehicle-side-list.component.scss'],
  providers: [DialogService],
})
export class VehicleSideListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: any = {};
  @Input() vehicleOdmHistory: any = {};
  @Input() highLightedVehicleId: string = '';
  @Input() defaultRegState: string = '';
  @Input() defaultRegStateCode: string = '';
  @Input() defaultMileageUnit: string = '';
  @Input() vehicleColors: any[] = [];
  @Input() driveTypes: any[] = [];
  @Input() defaultVehicles: any[] = [];
  @Input() vehicleTags: any[] = [];
  @Input() isVehiclesLoading: boolean = false;
  @Input() isTireStorageEnabled: boolean = false;
  @Input() isMotorLookupEnabled: boolean = false;
  @Input() openConnectMotor: boolean = false;
  @Input() highLightedVIN: boolean = false;
  @Input() highLightedMiles: boolean = false;
  @Input() customer: any = {};
  @Input() isLoadingAppointment: boolean = false;
  @Input() isCarfaxEnabled: boolean = false;
  @Input() vehicleSearchPlaceholder: string = '';
  @Input() totalRecords: number = 0;
  @Input() drivers: any = [];
  @Input('vehicles')
  set vehicles(data: any[]) {
    if (data) {
      this._vehicles = data;
    }
  }
  @Input('appts')
  set appts(data: any) {
    if (data) {
      this.customerAppointments = data;
    }
  }

  @Output() vehicleUpdate = new EventEmitter();
  @Output() updateTags = new EventEmitter();
  @Output() updateTagList = new EventEmitter();
  @Output() updateVehicleConnectedStatus = new EventEmitter();
  @Output() getCustomerVehicle = new EventEmitter();

  @ViewChild('vehiclesList')
  vehiclesList: ElementRef;

  _vehicles: any = [];
  customerAppointments: any;
  dataServiceSub: Subscription;
  subscriptions: Subscription[] = [];
  isHighlightedScrollingDone: boolean = false;

  constructor(
    private dataService: DataService,
    private dateService: DateService,
    private httpService: HTTPService,
    private helperService: HelperService,
    private dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.isHighlightedScrollingDone = false;
    this.subscriptions.push(
      this.dataService.getData().subscribe((obj) => {
        const response = obj.data;
        if (response.key === 'sidebar-vehicle-update') {
          if (response?.data?.customer) {
            this.customer = response.data.customer;
          }
          this.updateVehicles();
        }
      }),
    );

    this.onVehicleUpdate();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }

  ngOnChanges(change) {
    const { isVehiclesLoading } = change;

    if (isVehiclesLoading) {
      if (isVehiclesLoading.previousValue && !isVehiclesLoading.currentValue) {
        if (this.highLightedVehicleId && !this.isHighlightedScrollingDone) {
          this.isHighlightedScrollingDone = true;

          setTimeout(() => {
            try {
              const selectedEl = this.vehiclesList.nativeElement.querySelector(`#V${this.highLightedVehicleId}`) as HTMLElement;

              selectedEl.scrollIntoView({ block: 'start', inline: 'nearest' });
              selectedEl.focus();
            } catch (err) {}
          }, 700);
        }
      }
    }
  }

  onVehicleUpdate() {
    /** This subscriber is getting data from the part-add.component.ts */
    const dsSub = this.dataService.getData().subscribe((data) => {
      if (data) {
        if (data.data.type === 'passdata' && data.data.key === 'vehicle-list') {
          this.vehicleUpdate.emit();
        }
      }

      dsSub.unsubscribe();
    });

    this.subscriptions.push(dsSub);
  }

  updateVehicles() {
    const updatedVehicles = [];

    this._vehicles.forEach((vehicle: any, i: number) => {
      if (!isEqual(vehicle, this.defaultVehicles[i])) {
        let isValid = true;

        if (vehicle.year && (vehicle.model || vehicle.make)) isValid = false;
        if (this.dataService.isLicensePlateMandatory && !vehicle.licensePlateNo) isValid = false;
        if (this.dataService.isRegStateMandatory && !vehicle.registrationState) isValid = false;

        if (!isValid) updatedVehicles.push(this.formatVehicle(vehicle, i));
      }
    });

    if (updatedVehicles.length) {
      this.httpService.updateBulkVehicle({ vehicles: updatedVehicles, customerId: this.customer._id }).then(
        () => {
          this.dataService.sendData(
            this.dataService.typeSidebar('close', null, null, {
              isSuccess: true,
              vehicles: updatedVehicles,
              type: 'vehicle',
            }),
          );
        },
        (err) => {
          this.helperService.showToaster('error', err);
        },
      );
    }
  }

  formatVehicle(vehicle: any, index: number = -1) {
    const { vin, year, make, model, color, mileage, driveType, transmission } = vehicle;
    let odm: any = {};

    if (mileage) {
      vehicle.mileage = `${mileage || ''}`;

      if (!vehicle.mileageMeasuringUnit) {
        vehicle.mileageMeasuringUnit = this.defaultMileageUnit;
        vehicle.mileageMeasuringUnit = find(mileageUnits, {
          value: vehicle.mileageMeasuringUnit,
        });
      }

      odm = {
        reading: mileage,
        readingDate: this.dateService.currentDate(),
      };

      if (vehicle.mileageMeasuringUnit && vehicle.mileageMeasuringUnit.value) {
        odm.mileageMeasuringUnit = vehicle.mileageMeasuringUnit.value;
      }
    }

    if (vehicle.licensePlateNo && !vehicle.registrationStateCode) {
      vehicle.registrationState = this.defaultRegState;
      vehicle.registrationStateCode = this.defaultRegStateCode;
      vehicle.state = vehicle.registrationState;
    }

    vehicle.customerId = this.customer._id;
    vehicle.name = `${year} ${make} ${model}`;
    vehicle.vehicleName = vehicle.vehicleName || vehicle.name || '';

    // This is needed for the cases where driver is populated in the vehicle
    if(vehicle?.driver?._id) {
      vehicle.driver = {
        driverId: vehicle.driver._id,
        name: vehicle.driver.fullName,
      }
    }

    vehicle = {
      ...vehicle,
      year,
      make,
      model,
      VIN: vin,
      driveType: driveType && driveType.name,
      ODM: [odm],
      color: color && color.name,
      transmission: transmission && transmission.value,
    };

    return vehicle;
  }

  onAddVehicle() {
    /** This data is subscribe in header.component */
    this.dataService.sendData(this.dataService.typeDialog('add-vehicle', 'vehicle-side-list', this.user._id, this.user.fullNameQBO, this.user));
  }

  _updateTags(data) {
    this.updateTags.emit(data);
  }

  getVehicleUpdate(event: any) {
    this.vehicleUpdate.emit(event);
  }

  _updateVehicleConnectedStatus() {
    this.updateVehicleConnectedStatus.emit();
  }

  onScroll(event) {
    const element = event.target;

    if (element.scrollHeight - element.scrollTop - element.clientHeight < 10) {
      if (this._vehicles.length < this.totalRecords) {
        this.getCustomerVehicle.emit();
      }
    }
  }

  openAddTireStorageModal(vehicle: any) {
    const data = {
      customer: {
        customerId: this.customer._id,
        displayName: this.customer.fullNameQBO,
      },
      vehicle: {
        vehicleId: vehicle._id,
        name: vehicle.vehicleName ? vehicle.vehicleName : vehicle.name,
      },
    };

    const ref = this.dialogService.open(AddTireStorageModal, {
      styleClass: 'add-dialog tire-storage-dialog',
      showHeader: false,
      data: data,
    });

    const onClose = ref.onClose.subscribe(({ isSuccess }) => {
      onClose.unsubscribe();
    });
  }
}
